import {
  CUE_TYPE_HEADING,
  CUE_TYPE_GROUP,
} from '../../../constants/cue-types.ts'

type BaseCue = {
  id: string
  type: string
}

type StructuredCue = {
  id: string
  children?: {
    id: string
  }[]
}

type CueNumberingOptions = {
  startFrom?: number
  // Note: prefix is intentionally excluded — it only affects display labels,
  // not ordering, so it's irrelevant when resolving a position.
  padding?: number
}

function padIndex (index: number, padding: number): string {
  const str = String(index)
  return padding > 0
    ? '0'.repeat(Math.max(0, padding - str.length)) + str
    : str
}

export const getRealCueIndex = (
  displayIndex: string,
  sortedCues: BaseCue[],
  structuredData: StructuredCue[],
  options: CueNumberingOptions = {},
) => {
  const { startFrom = 1, padding = 1 } = options

  // Parse the input as a string to check if it contains a decimal
  const displayIndexStr = String(displayIndex)

  // Check if we're looking for a group item or a child item
  if (displayIndexStr.includes('.')) {
    // This is a request for a child item (e.g., 3.1 or 003.1)
    const [parentIndexStr, childIndexStr] = displayIndexStr.split('.')
    const childDisplayIndex = parseInt(childIndexStr, 10)

    if (isNaN(childDisplayIndex)) {
      return null // Invalid input
    }

    // Map to track which IDs are in groups (to skip them when counting)
    const childIdsInGroups = new Set()
    const groupIdMap: Record<string, string[]> = {}

    // First build a map of all child IDs in groups
    for (const item of structuredData) {
      if (item.children) {
        groupIdMap[item.id] = item.children.map((child) => child.id)
        item.children.forEach((child) => childIdsInGroups.add(child.id))
      }
    }

    let currentGroupIndex = startFrom - 1
    let foundGroupId = null

    // Find the parent group in the flat sorted array
    for (let i = 0; i < sortedCues.length; i++) {
      const item = sortedCues[i]

      // Skip headings
      if (item.type === CUE_TYPE_HEADING) {
        continue
      }

      // Skip items that are children of groups
      if (childIdsInGroups.has(item.id)) {
        continue
      }

      // Increment the display index for non-heading, non-child items
      currentGroupIndex += 1

      // If this is the parent group we're looking for
      if (
        padIndex(currentGroupIndex, padding) === parentIndexStr &&
        item.type === CUE_TYPE_GROUP
      ) {
        foundGroupId = item.id
        break
      }
    }

    if (!foundGroupId) return null // Parent group not found

    // Get the children IDs of this group
    const childrenIds = groupIdMap[foundGroupId] ?? []

    // Make sure the child index is valid
    if (childDisplayIndex <= 0 || childDisplayIndex > childrenIds.length) {
      return null // Child index out of range
    }

    // Get the target child ID
    const targetChildId = childrenIds[childDisplayIndex - 1]

    // Find this child's index in the sortedCues array
    for (let i = 0; i < sortedCues.length; i++) {
      if (sortedCues[i].id === targetChildId) {
        return i
      }
    }

    return null // Child not found in sortedCues
  } else {
    // This is a request for a main-level item

    // Map to track which IDs are in groups (to skip them when counting)
    const childIdsInGroups = new Set()

    // Build a set of all child IDs in groups
    for (const item of structuredData) {
      if (item.children) {
        item.children.forEach((child) => childIdsInGroups.add(child.id))
      }
    }

    let currentIndex = startFrom

    for (let i = 0; i < sortedCues.length; i++) {
      const item = sortedCues[i]

      // Skip headings in our indexing
      if (item.type === CUE_TYPE_HEADING) {
        continue
      }

      // Skip items that are groups or children of groups
      if (childIdsInGroups.has(item.id)) {
        continue
      }

      // If we've reached the target index, return this array index
      if (padIndex(currentIndex, padding) === displayIndexStr) {
        return i
      }

      // Increment after checking (so we start counting from startFrom)
      currentIndex += 1
    }

    return null // Index not found
  }
}
